/**
 * API Client Setup
 *
 * Initialize and configure API clients for browser environment.
 * Supports multiple backend services (oex, bff, nts).
 */

import Cookies from 'js-cookie';

import {
  setApiClient,
  type ApiClient,
  type ApiError,
} from '@shared-runtime/api';
import {
  ALL_SERVICES,
  getErrorMessage,
  getServiceBaseUrl,
  isStagingEnv,
  type ServiceName,
  type StagingEnv,
} from '@shared-runtime/config';
import { toast } from '@shared-web/utils/toast';

import { isUnauthorized } from '../../modules/auth/utils/isUnauthorized';
import { createBrowserApiClient } from './browserClient';
import { createSSRApiClient } from './ssrClient';

/**
 * Error deduplication for toast notifications.
 * Prevents the same error from being shown multiple times within a short period.
 */
const ERROR_DEDUP_TTL_MS = 1000; // 1 second
const recentErrors = new Map<string, number>();

/**
 * Check if an error was recently shown and should be deduplicated.
 * Returns true if the error should be shown, false if it should be skipped.
 */
function shouldShowError(errorKey: string): boolean {
  const now = Date.now();
  const lastShown = recentErrors.get(errorKey);

  // TODO: add OTEL logging for clients with rapidly increasing error counts
  // Clean up old entries periodically
  if (recentErrors.size > 50) {
    for (const [key, timestamp] of recentErrors) {
      if (now - timestamp > ERROR_DEDUP_TTL_MS) {
        recentErrors.delete(key);
      }
    }
  }

  if (lastShown && now - lastShown < ERROR_DEDUP_TTL_MS) {
    return false; // Error was recently shown, skip
  }

  recentErrors.set(errorKey, now);
  return true;
}

/**
 * Check if user has a valid token.
 */
function hasToken(): boolean {
  return !!Cookies.get('token');
}

/**
 * Get base URL for a service in current environment
 *
 * Environment resolution:
 * - MODE=development → Uses Vite proxy paths (/deriv, /bff, etc.)
 * - MODE=staging → Uses staging URLs based on VITE_APP_ENV (dstg, ddev, dpre)
 * - MODE=production → Uses production URLs
 */
function getBaseUrl(service: ServiceName): string {
  const mode = import.meta.env.MODE;
  const appEnv = import.meta.env.VITE_APP_ENV;

  // For staging mode, resolve the staging sub-environment from VITE_APP_ENV
  const stagingEnv: StagingEnv | undefined = isStagingEnv(appEnv)
    ? appEnv
    : undefined;

  return getServiceBaseUrl(service, mode, stagingEnv);
}

/**
 * API Clients object
 */
export interface ApiClients {
  oex: ApiClient;
  bff: ApiClient;
  nts: ApiClient;
  be: ApiClient;
  worker: ApiClient;
}

let apiClients: ApiClients | null = null;

/**
 * Get API clients
 * @throws Error if clients are not initialized
 *
 * @example
 * ```ts
 * const { bff, oex, nts } = getApiClients();
 * const tickers = await bff.get('/v1/tickers');
 * ```
 */
export function getApiClients(): ApiClients {
  if (!apiClients) {
    throw new Error(
      'API clients not initialized. Call setupBrowserApiClients() first.',
    );
  }
  return apiClients;
}

/**
 * Setup API clients for browser environment
 *
 * Initializes clients for all backend services:
 * - oex: OEX service
 * - bff: Backend-for-Frontend service
 * - nts: NTS service
 *
 * Features:
 * - Automatic token injection from cookie
 * - Toast notifications for errors (optional)
 *
 * @param options - Setup options
 * @param options.enableToast - Enable toast notifications (default: true)
 *
 * @example
 * // In app entry point
 * setupBrowserApiClients();
 *
 * @example
 * // Access clients
 * const { bff, oex, nts } = getApiClients();
 * const data = await bff.get('/v1/tickers');
 */
export function setupBrowserApiClients(
  options: {
    enableToast?: boolean;
  } = {},
): ApiClients {
  const { enableToast = true } = options;

  // Create clients for all services
  const clients = {} as ApiClients;

  for (const service of ALL_SERVICES) {
    // Error handler with service-specific error messages and unauthorized detection
    const onError = (error: ApiError) => {
      const isUnauthorizedError = isUnauthorized(error.code);
      const userHasToken = hasToken();

      // Handle unauthorized error

      if (isUnauthorizedError && !userHasToken) {
        // For 401 errors when user doesn't have a token, silently skip
        // This is expected behavior - user is not logged in
        return;
      }

      // Show toast error if enabled and error should be shown (deduplication)
      if (enableToast) {
        const errorKey = `${service}:${error.code}:${error.message}`;

        if (shouldShowError(errorKey)) {
          const message = getErrorMessage(service, error.code, error.message);
          toast.error(message);
        }
      }
    };

    const client = createBrowserApiClient({
      baseUrl: getBaseUrl(service),
      timeout: 30000,
      onError,
      service,
    });

    clients[service] = client;
    setApiClient(client, service); // For backward compatibility
  }

  apiClients = clients;
  return clients;
}

/**
 * Setup API clients for SSR environment (Cloudflare Workers)
 *
 * Initializes clients for all backend services in server-side rendering context.
 *
 * @param options - Setup options
 * @param options.getToken - Function to get auth token from request context
 *
 * @example
 * // In entry.server.tsx or loader
 * const clients = setupSSRApiClients({
 *   getToken: () => request.headers.get('cookie')?.match(/token=([^;]+)/)?.[1]
 * });
 */
export function setupSSRApiClients(
  options: {
    getToken?: () => string | undefined | Promise<string | undefined>;
  } = {},
): ApiClients {
  const { getToken } = options;

  // Common client config
  const commonConfig = {
    timeout: 30000,
    getToken,
  };

  // Create clients for all services
  const clients = {} as ApiClients;

  for (const service of ALL_SERVICES) {
    const client = createSSRApiClient({
      ...commonConfig,
      baseUrl: getBaseUrl(service),
      service,
    });

    clients[service] = client;
    setApiClient(client, service); // For backward compatibility
  }

  apiClients = clients;
  return clients;
}
